<template>
	<LoginModal
		v-if="!isUserLoggedIn"
		:is-submitting="isSubmitting"
		:auth-error="authError"
		:info-message="infoMessage"
		@google="signInWithGoogle"
		@microsoft="signInWithOutlook"
		@dismiss="leaveLogin"
	/>

	<form class="logout" @submit.prevent="logOut" v-else>
		<button type="submit">Log Out</button>
	</form>
</template>

<script lang="ts" setup>
import * as Sentry from "@sentry/browser"
import LoginModal from "@/components/modals/LoginModal.vue"
import { ref, onMounted, onUnmounted } from "vue"
import { useRouter, useRoute, LocationQueryValue } from "vue-router"
import { useI18n } from "vue-i18n"
import { UseRootStore, RedirectReason } from "@/model/RootStore"
import { storeToRefs } from "pinia"
import { validateIDToken } from "@/utils/ApiClient"
import { supabase } from "@/utils/supabaseClient"
import { finishLoginAndRedirect } from "@/utils/sessionSync"
import { Route } from "@/utils/routing"
import { useUIStore } from "@/stores/uiStore"
import { useOAuthSignIn } from "@/composables/useOAuthSignIn"
import { CtaSource, parseCtaSource } from "@/utils/ctaSource"
const store = UseRootStore()
const { isUserLoggedIn } = storeToRefs(store)
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// const isSignup = ref(false)

// const loginDetails = ref({
// 	email: "",
// 	password: "",
// })

// const signupDetails = ref({
// 	email: "",
// 	password: "",
// 	confirmPassword: "",
// })

const uiStore = useUIStore()
const { isSubmitting, authError, signInWithGoogle, signInWithOutlook } = useOAuthSignIn(
	() => route.query.redirect as string | undefined,
	parseCtaSource(route.query.source) ?? CtaSource.LoginPage
)
const infoMessage = ref("")

onMounted(async () => {
	// Check for reason parameter (we won't redirect to login for expired projects or access denied)
	const reason: LocationQueryValue | LocationQueryValue[] = route.query.reason
	if (reason === RedirectReason.LoginRequired) {
		infoMessage.value = t("project.login-required-message")
	}
	// If we are in the logout route then handle the logout
	if (window.location.pathname === "/logout") {
		await handleLogoutFromRoute()
		return
	}

	// Already logged in — sync dashboard cookie if needed, then follow redirect
	const redirectPath = route.query.redirect as string | undefined
	if (store.userApiToken) {
		const result = await finishLoginAndRedirect(store.userApiToken, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
		return
	}

	// If token was revoked, sign out from Supabase
	const urlParams = new URLSearchParams(window.location.search)
	if (urlParams.get("revoked") === "true") {
		await supabase.auth.signOut()
		urlParams.delete("revoked")
		const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : "")
		window.history.replaceState({}, "", newUrl)
		return
	}

	try {
		// Let's use this in case we are still logged in with Supabase
		const {
			data: { session },
			error,
		} = await supabase.auth.getSession()

		if (error) {
			console.error("Error getting session:", error)
			authError.value = "Session error: " + error.message
			return
		}

		// If we have a session from OAuth callback, validate it with our backend
		if (session?.access_token && !store.userApiToken) {
			await handleOAuthCallback(session.access_token)
		}
	} catch (error) {
		console.error("Error importing Supabase client:", error)
		authError.value = "Error importing Supabase client: " + (error as Error).message
	}

	// Set up auth state listener for OAuth callbacks
	try {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			console.log("Auth state change:", event, session?.user?.email)

			if (event === "SIGNED_IN" && session?.access_token && !store.userApiToken) {
				await handleOAuthCallback(session.access_token)
			}
		})

		// Clean up subscription
		onUnmounted(() => {
			subscription.unsubscribe()
		})
	} catch (error) {
		console.error("Error setting up auth listener:", error)
	}
})

// const logIn = async () => {
// 	isSubmitting.value = true
// 	authError.value = ""

// 	try {
// 		// Everything begins here:
// 		// Step 1: Authenticate with Supabase
// 		const { data, error } = await supabase.auth.signInWithPassword({
// 			email: loginDetails.value.email,
// 			password: loginDetails.value.password,
// 		})

// 		if (error) {
// 			authError.value = error.message
// 			return
// 		}

// 		if (!data.session?.access_token) {
// 			authError.value = "Authentication failed"
// 			return
// 		}

// 		// Step 2: Pass the token to the backend for further validation and also get our own JWT and roles, etc
// 		const validationResult = await validateIDToken(data.session.access_token)

// 		if (!validationResult.success) {
// 			authError.value = validationResult.error || "Backend validation failed"
// 			return
// 		}

// 		// Step 3: We got the token and time to save it and continue
// 		store.userApiToken = validationResult.token
// 		router.push("/")
// 	} catch (error) {
// 		console.error("Error during login:", error)
// 		authError.value = "Login failed. Please try again."
// 	} finally {
// 		isSubmitting.value = false
// 	}
// }

// const signUp = async () => {
// 	isSubmitting.value = true
// 	authError.value = ""

// 	// Validate passwords match
// 	if (signupDetails.value.password !== signupDetails.value.confirmPassword) {
// 		authError.value = "Passwords do not match"
// 		isSubmitting.value = false
// 		return
// 	}

// 	// Validate password strength
// 	if (signupDetails.value.password.length < 6) {
// 		authError.value = "Password must be at least 6 characters long"
// 		isSubmitting.value = false
// 		return
// 	}

// 	try {
// 		// Step 1: Sign up with Supabase
// 		const { data, error } = await supabase.auth.signUp({
// 			email: signupDetails.value.email,
// 			password: signupDetails.value.password,
// 		})

// 		if (error) {
// 			authError.value = error.message
// 			return
// 		}

// 		if (!data.session?.access_token) {
// 			// We should think about this. But that would require doing SMTP configuration which is not difficult
// 			// Find out who is our email sender
// 			authError.value = "Please check your email to confirm your account"
// 			return
// 		}

// 		// Step 2: Validate Supabase token with our backend and get our JWT
// 		const validationResult = await validateIDToken(data.session.access_token)

// 		if (!validationResult.success) {
// 			authError.value = validationResult.error || "Backend validation failed"
// 			return
// 		}

// 		// Step 3: Save our JWT token and redirect
// 		store.userApiToken = validationResult.token
// 		router.push("/")
// 	} catch (error) {
// 		console.error("Error during signup:", error)
// 		authError.value = "Signup failed. Please try again."
// 	} finally {
// 		isSubmitting.value = false
// 	}
// }

const handleLogoutFromRoute = async () => {
	try {
		await store.logout()

		// Should we redirect to login or back to the map?
		router.push("/login")
	} catch (error) {
		console.error("Error during logout from route:", error)
		router.push("/login")
	}
}

const handleOAuthCallback = async (accessToken: string) => {
	if (store.userApiToken) {
		const redirectPath = route.query.redirect as string | undefined
		const result = await finishLoginAndRedirect(store.userApiToken, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
		return
	}

	isSubmitting.value = true
	authError.value = ""

	try {
		// Step 1: Validate Supabase token with our backend and get our JWT
		const {
			data: { user },
			error: userError,
		} = await supabase.auth.getUser(accessToken)

		if (userError || !user?.email) {
			authError.value = "Failed to get user information"
			return
		}

		// // Step 2: only allow @landerz.ca emails and nothign else
		// if (!user.email || !user.email.endsWith("@landerz.ca")) {
		// 	authError.value = "Only @landerz.ca email addresses are allowed."
		// 	await supabase.auth.signOut()
		// 	return
		// }

		// const emailDomain = user.email.split("@")[1]
		// if (emailDomain !== "landerz.ca") {
		// 	authError.value = "Only @landerz.ca email addresses are allowed."
		// 	await supabase.auth.signOut()
		// 	return
		// }

		// Step 3: Validate Supabase token with our backend and get our JWT
		const validationResult = await validateIDToken(accessToken)

		if (!validationResult.success) {
			authError.value = validationResult.error || "Backend validation failed"
			return
		}

		// Step 4: Save our JWT token and redirect
		store.userApiToken = validationResult.token
		const redirectPath = route.query.redirect as string | undefined
		const result = await finishLoginAndRedirect(validationResult.token, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
	} catch (error) {
		console.error("Error during OAuth callback:", error)
		Sentry.captureException(error)
		authError.value = "OAuth authentication failed. Please try again."
	} finally {
		isSubmitting.value = false
	}
}

// const signInWithLinkedIn = () => {
// 	console.log("LinkedIn sign in - not implemented yet")
// }

function leaveLogin(): void {
	uiStore.dismissWelcome()
	const hasPreviousPage = window.history.state?.back != null
	if (hasPreviousPage && route.query.reason !== RedirectReason.LoginRequired) {
		router.back()
	} else {
		router.push({ name: Route.Home })
	}
}

async function logOut() {
	await store.logout()
	router.push("/login")
}
</script>

<style scoped>
.logout {
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: var(--spacing-xl);
	background-color: var(--overlay-backdrop);

	button {
		padding: var(--spacing-md) var(--spacing-xl);
		background: var(--primary-green);
		color: var(--neutral-0);
		border: none;
		border-radius: var(--border-radius-md);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		transition: background 0.2s ease;

		&:hover {
			background: var(--primary-green);
			box-shadow: var(--shadow-md);
		}
	}
}
</style>
